import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../../shared/api/auth';
import { ApiError } from '../../shared/api/client';

/**
 * Reset-password page - reads the one-time token from the URL query (the
 * email link is /reset-password?token=…), collects a new password with live
 * complexity feedback, and POSTs to /auth/reset.
 *
 * On success the server has revoked every refresh token, so the user is
 * logged out everywhere and must sign in with the new password. We route
 * straight to /login with a success banner.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pwLong = password.length >= 12;
  const pwUpper = /[A-Z]/.test(password);
  const pwLower = /[a-z]/.test(password);
  const pwDigit = /[0-9]/.test(password);
  const pwValid = pwLong && pwUpper && pwLower && pwDigit;
  const pwMatch = password.length > 0 && password === password2;

  const canSubmit = useMemo(
    () => token.length > 0 && pwValid && pwMatch && !busy,
    [token, pwValid, pwMatch, busy],
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setBusy(true);
    try {
      await resetPassword(token, password);
      navigate('/login?reset=success', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.body.message);
      else setError('Could not reset the password. The link may have expired.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title="Reset password" subtitle="Choose a new one">
      {token.length === 0 ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-3 text-sm text-rose-300">
            This link is missing its reset token. Request a new one to continue.
          </div>
          <Link
            to="/forgot-password"
            className="btn-primary w-full inline-flex justify-center"
          >
            Request a new link
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <div className="label mb-1">New password</div>
            <input
              type="password"
              autoComplete="new-password"
              className="input"
              placeholder="At least 12 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <ul className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
              <PwRule ok={pwLong} label="≥ 12 characters" />
              <PwRule ok={pwUpper} label="Uppercase letter" />
              <PwRule ok={pwLower} label="Lowercase letter" />
              <PwRule ok={pwDigit} label="A digit" />
            </ul>
          </label>

          <label className="block">
            <div className="label mb-1">Confirm new password</div>
            <input
              type="password"
              autoComplete="new-password"
              className="input"
              placeholder="Re-type the password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              required
            />
            {password2.length > 0 && !pwMatch && (
              <p className="text-[11px] text-rose-400 mt-1">
                Passwords don&apos;t match.
              </p>
            )}
          </label>

          {error && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
            {busy ? 'Updating…' : 'Update password'}
          </button>

          <p className="mt-3 text-xs text-slate-400 text-center">
            <Link to="/login" className="link-brand">
              Back to sign in
            </Link>
          </p>
        </form>
      )}
    </Shell>
  );
}

function PwRule({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className={ok ? 'text-emerald-400' : 'text-slate-500'}>
      <span className="mr-1">{ok ? '✓' : '○'}</span>
      {label}
    </li>
  );
}

function Shell(props: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div
      className="relative min-h-full w-full flex items-center justify-center p-6"
      style={{
        background:
          'linear-gradient(135deg, #0f172a 0%, #11233f 55%, #0e2a3a 100%)',
      }}
    >
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-slate-900 p-6 shadow-soft-lg">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-lg bg-gradient-brand text-white flex items-center justify-center font-semibold">
            T
          </div>
          <div>
            <div className="text-base font-semibold text-slate-100">{props.title}</div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-accent-300/80">
              {props.subtitle}
            </div>
          </div>
        </div>
        {props.children}
      </div>
    </div>
  );
}
